<template>
  <section
    class="InviteCard"
    data-theme="light"
    :aria-label="name ? `Share card for ${name}` : 'Share card to join the crew'"
  >
    <h2
      v-if="name"
      class="InviteCard__crewName"
    >
      {{ name }}
    </h2>
    <div class="InviteCard__qrCodeContainer">
      <figure
        class="InviteCard__qrCode"
      >
        <div
          class="InviteCard__qrCodeSvgContainer"
          v-html="qrSvg"
        />
        <div class="InviteCard__qrCodeBasketballContainer">
          <Basketball class="InviteCard__qrCodeBasketball" />
        </div>
      </figure>
    </div>
    <template v-if="!excludeInstructions">
      <h1 class="InviteCard__title">
        Scan to join the crew
      </h1>
      <div class="InviteCard__divider">
        or
      </div>
    </template>
    <FormButton @click="handleShareLink">
      <span v-if="!isUrlCopied">
        Copy the invite link
      </span>
      <span v-else>
        Copied!
      </span>
    </FormButton>
  </section>
</template>

<script lang="ts" setup>
import Basketball from "@/components/Basketball.vue";
import FormButton from "@/components/FormButton.vue";
import haptic from "@/lib/utils/platform/haptic";
import delay from "@shared/lib/utils/async/delay";
import QRCode from "qrcode";
import { ref, watch } from "vue";

const {
  joinUrl,
} = defineProps<{
  joinUrl: string,
  name?: string,
  excludeInstructions?: boolean,
}>();

const qrSvg = ref("");
const isUrlCopied = ref(false);

watch(() => joinUrl, async (newJoinUrl) => {
  qrSvg.value = await QRCode.toString(newJoinUrl, {
    type: "svg",
    errorCorrectionLevel: "H",
    margin: 2,
  });
}, {
  immediate: true,
});

async function handleShareLink () {
  haptic();
  await navigator.clipboard.writeText(joinUrl);
  isUrlCopied.value = true;
  await delay(1000);
  isUrlCopied.value = false;
}
</script>

<style lang="scss" scoped>
.InviteCard {
  padding: 24px 20px;

  border-radius: 16px;
  background-color: var(--themeColorNeutral0);

  scroll-snap-align: center;
  scroll-snap-stop: always;
}

.InviteCard__crewName {
  margin-bottom: calc(22px - var(--fontSafariOffset));

  font-size: 16px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-align: center;
}

.InviteCard__qrCodeContainer {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.InviteCard__qrCode {
  position: relative;

  flex: 1 1 auto;
  max-width: 400px;
  overflow: hidden;

  border: solid 1px var(--themeColorNeutral100);
  border-radius: 8px;
}

.InviteCard__qrCodeSvgContainer {
  :deep(svg) {
    width: 100%;
    height: 100%;
  }
}

.InviteCard__qrCodeBasketballContainer {
  --pixelSize: 10;
  --size: calc(var(--pixelSize) / 49 * 100%);
  --borderSize: calc(1 / var(--pixelSize) * 100%);
  --center: calc(50% - var(--size) / 2);
  position: absolute;
  top: var(--center);
  left: var(--center);
  isolation: isolate;

  width: var(--size);
  height: var(--size);

  &::before {
    position: absolute;
    inset: calc(-2 * var(--borderSize));
    z-index: -1;

    border-radius: 50%;
    background-color: var(--colorOffBlack);

    content: "";
  }

  &::after {
    position: absolute;
    inset: calc(-1 * var(--borderSize));
    z-index: -1;

    border-radius: 50%;
    background-color: var(--colorOffWhite);

    content: "";
  }
}

.InviteCard__qrCodeBasketball {
  width: 100%;
  height: 100%;
}

.InviteCard__title {
  color: var(--themeColorNeutral900);

  font-size: 24px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-align: center;
}

.InviteCard__divider {
  position: relative;
  isolation: isolate;

  margin: 16px 0 24px;

  color: var(--themeColorNeutral400);

  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-align: center;

  &::before {
    position: absolute;
    top: 50%;
    left: 0;
    z-index: -1;

    width: 100%;

    border-top: solid 1px var(--themeColorNeutral100);

    content: "";
  }

  &::after {
    position: absolute;
    left: 50%;
    z-index: -1;
    transform: translateX(-50%);

    width: 36px;
    height: 100%;

    background-color: var(--themeColorNeutral0);

    content: "";
  }
}
</style>
